import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import pMemoize from 'p-memoize';
import { AppError, OperationAbortedError } from '@app/domain/entities';
import type { BankStatementCachePort } from '@app/ui/shared';
import { MonobankClientInfo, MonobankStatement } from './monobank-api.models';
import type { MonobankApiConfig } from './tokens';

const MONOBANK_API_DEFAULTS = {
  apiUrl: 'https://api.monobank.ua',
  rateLimitMs: 60_000,
} satisfies Required<MonobankApiConfig>;

/**
 * Thrown when a Monobank API call fails (HTTP error, timeout, or network
 * failure). `endpoint` is a short identifier such as `'monobank-client-info'`
 * or `'monobank-statements'` so AppErrorHandler can log the exact call.
 */
export class MonobankApiError extends AppError {
  readonly code = 'E_MONOBANK_API';

  constructor(
    public readonly endpoint: string,
    message?: string,
    cause?: unknown,
  ) {
    super(message ?? `Monobank API request failed: ${endpoint}`, { endpoint }, { cause });
  }
}

export class MonobankApiService {
  private lastRequestTimeByAccount = new Map<string, number>();
  public readonly config: Required<MonobankApiConfig>;

  constructor(
    private readonly http: HttpClient,
    private readonly bankCache: BankStatementCachePort,
    config?: MonobankApiConfig,
  ) {
    this.config = {
      apiUrl: config?.apiUrl ?? MONOBANK_API_DEFAULTS.apiUrl,
      rateLimitMs: config?.rateLimitMs ?? MONOBANK_API_DEFAULTS.rateLimitMs,
    };
  }

  /**
   * Get client information including list of accounts.
   * Cached for 1 hour.
   */
  getClientInfo = pMemoize(
    async (token: string): Promise<MonobankClientInfo> => {
      try {
        const response = await firstValueFrom(
          this.http.get<MonobankClientInfo>(`${this.config.apiUrl}/personal/client-info`, {
            headers: { 'X-Token': token },
          }),
        );
        return response;
      } catch (error) {
        throw new MonobankApiError('/personal/client-info', 'Failed to fetch Monobank client info', error);
      }
    },
    {
      maxAge: 60 * 60 * 1000, // 1 hour
      cacheKey: () => `client-info`,
    },
  );

  /**
   * Fetch account statements for a given date range.
   * Checks cache first, then fetches from API if needed.
   */
  async fetchStatements(
    token: string,
    accountId: string,
    from: number,
    to: number,
    signal?: AbortSignal,
  ): Promise<MonobankStatement[]> {
    try {
      const cachedStatements = await this.bankCache.checkCoverage<MonobankStatement>('monobank', accountId, from, to);
      if (cachedStatements) {
        return cachedStatements;
      }
    } catch (cacheError) {
      this.logCacheFailure('Cache check failed, falling back to API', accountId, from, to, cacheError);
    }

    await this.enforceRateLimit(accountId);

    if (signal?.aborted) throw new OperationAbortedError();

    try {
      const response = await firstValueFrom(
        this.http.get<MonobankStatement[]>(`${this.config.apiUrl}/personal/statement/${accountId}/${from}/${to}`, {
          headers: { 'X-Token': token },
        }),
      );

      try {
        await this.bankCache.saveRange('monobank', accountId, from, to, response);
      } catch (cacheError) {
        this.logCacheFailure('Failed to save statements to cache', accountId, from, to, cacheError);
      }

      return response;
    } catch (error) {
      if (signal?.aborted) throw new OperationAbortedError();
      throw new MonobankApiError('/personal/statement', 'Failed to fetch Monobank statements', error);
    }
  }

  /**
   * Returns the number of milliseconds that the next API call for a given account
   * will need to wait before it is allowed by the rate limit. Returns 0 if no wait is needed.
   */
  getRateLimitWaitMs(accountId: string): number {
    const lastRequestTime = this.lastRequestTimeByAccount.get(accountId) ?? 0;
    const timeSinceLastRequest = Date.now() - lastRequestTime;
    return Math.max(0, this.config.rateLimitMs - timeSinceLastRequest);
  }

  private async enforceRateLimit(accountId: string): Promise<void> {
    const now = Date.now();
    const lastRequestTime = this.lastRequestTimeByAccount.get(accountId) ?? 0;
    const timeSinceLastRequest = now - lastRequestTime;

    if (timeSinceLastRequest < this.config.rateLimitMs) {
      const waitTime = this.config.rateLimitMs - timeSinceLastRequest;
      await this.delay(waitTime);
    }

    this.lastRequestTimeByAccount.set(accountId, Date.now());
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private logCacheFailure(message: string, accountId: string, from: number, to: number, error: unknown): void {
    const payload: Record<string, unknown> = { source: 'monobank', accountId, from, to };

    if (error instanceof Error) {
      payload['errorName'] = error.name;
      payload['errorMessage'] = error.message;
    } else {
      payload['errorType'] = typeof error;
      payload['errorValue'] = String(error);
    }

    if (error instanceof AppError) {
      payload['errorCode'] = error.code;
      payload['errorContext'] = error.context;
      payload['errorCause'] = error.cause;
    }

    console.warn(`[MonobankApiService] ${message}`, payload, error);
  }
}
